import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Button } from 'components/Button';
import { AutoSuggest } from 'components/Form/AutoSuggest';
import { Modal } from 'components/Modal';
import { useCart } from 'shared/CartContext';
import { useGetReaders } from './gql/queries/useGetReaders';

const Container = styled.div`
  padding: 20px;
`;

const ButtonRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-evenly;
  margin-top: 20px;
  width: 100%;
`;

const ReaderEntry = styled.div`
  display: flex;
  align-items: start;
  flex-direction: column;
  margin-bottom: 15px;
  padding: 15px 30px;
  border-radius: 5px;
  background-color: ${(props) =>
    props.isSelected ? props.theme.colors.primary : 'transparent'};
  border: 1px solid #ccc;
  color: ${(props) => (props.isSelected ? '#fff' : '#000')};
  gap: 10px;
  font-size: 18px;
  cursor: pointer;

  :hover {
    background-color: ${(props) => props.theme.colors.primary};
    color: #fff;
  }
`;

export const ReaderSelection = ({
  isVisible = true,
  setIsVisible,
  setIsCheckoutVisible,
  asModal = true,
  account_id,
  handleChangeReader,
}) => {
  const { accountId } = useCart();
  const { data } = useGetReaders(accountId ? accountId : account_id, isVisible);

  const [readers, setReaders] = useState([]);
  const [selectedReader, setSelectedReader] = useState(null);

  useEffect(() => {
    if (isVisible && data) {
      const readers = data.getAccount?.readers;

      if (readers?.length > 0) {
        if (readers.length === 1) {
          if (handleChangeReader) {
            handleChangeReader(readers[0]);
            setSelectedReader(readers[0]);
          } else {
            document.cookie = `${accountId ? accountId : account_id}-reader=${
              readers[0].reader_id
            }; expires=${new Date(
              new Date().getTime() + 12 * 60 * 60 * 1000
            ).toUTCString()}; path=/`;

            setIsVisible(false);
            setIsCheckoutVisible(true);
          }
        } else setReaders(readers);
      }
    }
  }, [
    accountId,
    isVisible,
    data,
    setIsVisible,
    setIsCheckoutVisible,
    handleChangeReader,
    selectedReader,
    account_id,
  ]);

  // If the reader is set in cookies, set it as selected
  useEffect(() => {
    const readerCookie = document.cookie
      .split('; ')
      .find((row) =>
        row.startsWith(`${accountId ? accountId : account_id}-reader=`)
      );

    if (readerCookie) {
      const readerId = readerCookie.split('=')[1];
      const reader = readers.find((reader) => reader.reader_id === readerId);

      if (reader && handleChangeReader) {
        setSelectedReader({
          label: `${reader.label} - ${reader.reader_id}`,
          value: reader,
        });
        if (handleChangeReader) {
          handleChangeReader({
            label: `${reader.label} - ${reader.reader_id}`,
            value: reader,
          });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, account_id, readers]);

  const handleClick = (reader) => {
    setSelectedReader(reader);
  };

  const handleContinue = () => {
    // Save the selected reader id to cookies with an expiration time of 12 hours
    document.cookie = `${accountId}-reader=${
      selectedReader.reader_id
    }; expires=${new Date(
      new Date().getTime() + 12 * 60 * 60 * 1000
    ).toUTCString()}; path=/`;

    setIsVisible(false);
    setIsCheckoutVisible(true);
  };

  const handleClose = () => {
    setIsVisible(false);
  };

  const modalContent = (
    <Container>
      {readers?.map((reader) => (
        <ReaderEntry
          key={reader.id}
          isSelected={selectedReader && reader.id === selectedReader.id}
          onClick={() => handleClick(reader)}
        >
          <span style={{ fontWeight: 600 }}>{reader.label}</span>
          <span style={{ fontWeight: 500 }}>{reader.reader_id}</span>
        </ReaderEntry>
      ))}

      <ButtonRow>
        <Button
          type="button"
          onClick={handleContinue}
          disabled={!selectedReader}
        >
          Continue
        </Button>
      </ButtonRow>
    </Container>
  );

  const dropdownContent = readers ? (
    <AutoSuggest
      id="reader"
      label="Select Reader"
      placeholder="Choose a reader"
      options={readers.map((reader) => ({
        label: `${reader.label} - ${reader.reader_id}`,
        value: reader,
      }))}
      onChange={(value) => {
        setSelectedReader(value);
        handleChangeReader(value);
      }}
      value={selectedReader}
    />
  ) : null;

  return asModal ? (
    <Modal
      isVisible={isVisible}
      hideModal={handleClose}
      title={`Select your credit card reader from the list below`}
      maxWidth={600}
    >
      {modalContent}
    </Modal>
  ) : (
    dropdownContent
  );
};
